import { profileQueryOptions, profileSelectors } from "@stryker/core/queries";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import DesktopQuoteDetailsView from "@/components/quotes/DesktopQuoteDetailsView";
import MobileQuoteDetailsView from "@/components/quotes/MobileQuoteDetailsView";
import QuoteCostBreakdown from "@/components/quotes/QuoteCostBreakdown";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import type { QuoteFormSchema } from "@/lib/schemas/createUpdateQuoteSchema";
import { cn } from "@/lib/utils";
import { getMargin } from "@/utils/calculations";

type QuoteDetailsTableProps = {
  repairSystemName?: string;
  repairSystemId?: string | null;
  isFetchingInitialQuoteData: boolean;
};
export default function QuoteDetailsTable({
  repairSystemName,
  repairSystemId,
  isFetchingInitialQuoteData,
}: QuoteDetailsTableProps) {
  const { control, setValue, watch } = useFormContext<QuoteFormSchema>();

  const { data: canViewCostAndMargin } = useQuery({
    ...profileQueryOptions(),
    select: profileSelectors.canViewCostAndMargin,
  });

  const [isMobile, setIsMobile] = useState(false);
  const [isRevenueOverrideEnabled, setIsRevenueOverrideEnabled] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint is 768px
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "quoteDetails",
  });

  const quoteDetails = watch("quoteDetails");
  const isQuoteLumpSum = watch("isQuoteLumpSum");
  const totalRevenueOverride = watch("totalRevenueOverride");
  const totalRevenue = quoteDetails.reduce((total, item) => total + item.quantity * item.price_per_unit, 0);
  const totalCost = quoteDetails.reduce((total, item) => total + item.quantity * item.cost_per_unit, 0);

  const hasOverride = totalRevenueOverride !== null && totalRevenueOverride !== undefined;
  const shouldUseOverride = isRevenueOverrideEnabled && hasOverride;
  const effectiveTotal = shouldUseOverride ? totalRevenueOverride : totalRevenue;
  const preAdjustedMargin = getMargin({
    totalQuotes: totalRevenue,
    totalCosts: totalCost,
  });
  const newMargin = getMargin({
    totalQuotes: effectiveTotal,
    totalCosts: totalCost,
  });

  useEffect(() => {
    if (hasOverride) {
      setIsRevenueOverrideEnabled(true);
    }
  }, [hasOverride]);

  const handleRevenueOverrideToggle = (checked: boolean) => {
    setIsRevenueOverrideEnabled(checked);

    if (!checked) {
      setValue("totalRevenueOverride", null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  };

  const tableBorder = "border border-gray-300";

  return (
    <Card className="p-4 md:p-10">
      <CardTitle>
        <Typography className="mb-2 text-sm md:text-base">
          <span className="font-bold">Repair System:</span> {repairSystemName}
        </Typography>
        Quote Details
      </CardTitle>
      <CardContent className="p-0 pt-4 md:pt-10">
        {/* MOBILE */}
        {isMobile && (
          <MobileQuoteDetailsView
            isFetchingInitialQuoteData={isFetchingInitialQuoteData}
            fields={fields}
            remove={remove}
            canViewCostAndMargin={!!canViewCostAndMargin}
            repairSystemId={repairSystemId}
          />
        )}

        {/* DESKTOP */}
        {!isMobile && (
          <DesktopQuoteDetailsView
            isFetchingInitialQuoteData={isFetchingInitialQuoteData}
            tableBorder={tableBorder}
            fields={fields}
            remove={remove}
            canViewCostAndMargin={!!canViewCostAndMargin}
            repairSystemId={repairSystemId}
          />
        )}

        <Button
          type="button"
          disabled={isFetchingInitialQuoteData}
          onClick={() =>
            append({
              item_name: "",
              quantity: 1,
              unit: "",
              cost_per_unit: 0,
              price_per_unit: 0,
              is_editable: true,
            })
          }
          className="mt-2 rounded bg-orange-500 px-4 py-2 text-sm text-white md:text-base"
        >
          + Item
        </Button>

        <div className="mt-4 border-gray-300 border-t pt-4 lg:pr-[101px]">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {canViewCostAndMargin && (
              <div className="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-2 py-1">
                <span className="p-2 font-bold text-sm md:text-base">Total Cost:</span>
                <span className="p-2 text-sm md:text-base">{Number.isNaN(totalCost) ? "N/A" : `$${totalCost}`}</span>
              </div>
            )}
            <div className="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-2 py-1">
              <span className="p-2 font-bold text-sm md:text-base">Total Revenue:</span>
              <span className="p-2 text-sm md:text-base">
                {Number.isNaN(totalRevenue) ? "N/A" : `$${totalRevenue}`}
              </span>
            </div>
            {shouldUseOverride && (
              <div className="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-2 py-1">
                <span className="p-2 font-bold text-sm md:text-base">New total:</span>
                <span className="p-2 text-sm md:text-base">${effectiveTotal}</span>
              </div>
            )}
            <div className="rounded-lg border border-orange-100 bg-orange-50/60 p-3 shadow-sm sm:col-span-2 xl:col-span-3">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="flex gap-3">
                  <Switch
                    checked={isRevenueOverrideEnabled}
                    onCheckedChange={handleRevenueOverrideToggle}
                    aria-label="Use optional quote revenue override"
                    className="mt-1"
                  />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm md:text-base">Use optional revenue override</p>
                    <p className="mt-1 text-gray-600 text-xs md:text-sm">
                      Turn this on only when the quote needs a manual revenue total. If it stays off, the quote uses the
                      calculated item total.
                    </p>
                  </div>
                </div>

                {isRevenueOverrideEnabled && (
                  <FormField
                    control={control}
                    name="totalRevenueOverride"
                    render={({ field, fieldState }) => (
                      <FormItem className="md:min-w-[220px]">
                        <FormLabel className="font-semibold text-gray-900 text-sm">Manual quote total</FormLabel>
                        <FormControl>
                          <TextInput
                            type="number"
                            step={1}
                            placeholder="Optional"
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const raw = (e.target as HTMLInputElement).value;
                              field.onChange(raw === "" ? null : Number(raw));
                            }}
                            onBlur={field.onBlur}
                            error={fieldState.error?.message}
                            startAdornment={<span className="text-gray-500">$</span>}
                            inputWrapperClassName="bg-white"
                            className="w-full"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </div>
            <FormField
              control={control}
              name="isQuoteLumpSum"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-4 py-3">
                  <div>
                    <FormLabel className="font-semibold text-gray-900 text-sm md:text-base">Show as lump sum</FormLabel>
                    <p className="mt-1 text-gray-600 text-xs">Display the quote as one combined total.</p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={!!field.value}
                      onCheckedChange={field.onChange}
                      aria-label="Show quote as lump sum"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {isQuoteLumpSum && <QuoteCostBreakdown />}
            {canViewCostAndMargin && (
              <div className="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-2 py-1">
                <span className="p-2 font-bold text-sm md:text-base">
                  {shouldUseOverride ? "Pre-adjusted Margin:" : "Margin:"}
                </span>
                <span
                  className={cn(
                    "p-2 text-sm md:text-base",
                    preAdjustedMargin < 0 ? "bg-red-600 text-white" : "bg-green-300"
                  )}
                >
                  {preAdjustedMargin.toFixed(2)}%
                </span>
              </div>
            )}
            {canViewCostAndMargin && shouldUseOverride && (
              <div className="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-2 py-1">
                <span className="p-2 font-bold text-sm md:text-base">New Margin:</span>
                <span
                  className={cn("p-2 text-sm md:text-base", newMargin < 0 ? "bg-red-600 text-white" : "bg-green-300")}
                >
                  {newMargin.toFixed(2)}%
                </span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
